import { createTheme, type Theme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";
import { trTR as coreTrTR } from "@mui/material/locale";
import { trTR as gridTrTR } from "@mui/x-data-grid/locales";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { amber } from "@mui/material/colors";
import type { ThemeMode } from "./brand";

// Tek sabit tema: marka moru (logo). Renk seçimi kaldırıldı.
// README (public/brand): degrade #7B6BEF → #6C5CE7, birincil mor #6C5CE7.
const BRAND_PRIMARY = {
  light: "#7B6BEF",
  main:  "#6C5CE7",
  dark:  "#574AC9",
};

// Karanlık temada okunabilirlik için marka morunun daha açık tonları.
const BRAND_PRIMARY_DARK = {
  light: "#A99CF5",
  main:  "#8B7CF0",
  dark:  "#6C5CE7",
};

// Yumuşak, düşük-alfa, katmanlı gölge skalası (MUI'nin sert Material gölgeleri yerine).
// Her yükseltilmiş yüzey/menu/dialog anında "premium" görünür. MUI 25 elemanlı Shadows bekler.
const buildShadows = (isLight: boolean): Shadows => {
  const key = isLight
    ? [
        "0 1px 2px rgba(23,23,60,.05), 0 1px 3px rgba(23,23,60,.06)",
        "0 2px 6px rgba(23,23,60,.06), 0 1px 4px rgba(23,23,60,.05)",
        "0 4px 12px rgba(23,23,60,.07), 0 2px 6px rgba(23,23,60,.05)",
        "0 6px 16px rgba(23,23,60,.08), 0 3px 8px rgba(23,23,60,.05)",
        "0 10px 24px rgba(23,23,60,.10), 0 4px 10px rgba(23,23,60,.06)",
        "0 16px 36px rgba(23,23,60,.12), 0 6px 14px rgba(23,23,60,.07)",
        "0 24px 52px rgba(23,23,60,.16), 0 10px 22px rgba(23,23,60,.08)",
      ]
    : [
        "0 1px 2px rgba(0,0,0,.5)",
        "0 2px 6px rgba(0,0,0,.55)",
        "0 4px 12px rgba(0,0,0,.6)",
        "0 8px 20px rgba(0,0,0,.62)",
        "0 12px 28px rgba(0,0,0,.66)",
        "0 18px 40px rgba(0,0,0,.7)",
        "0 26px 56px rgba(0,0,0,.75)",
      ];
  const bucket = (i: number) =>
    i <= 1 ? 0 : i <= 2 ? 1 : i <= 3 ? 2 : i <= 5 ? 3 : i <= 7 ? 4 : i <= 15 ? 5 : 6;
  return Array.from({ length: 25 }, (_, i) => (i === 0 ? "none" : key[bucket(i)])) as unknown as Shadows;
};

export const buildTheme = (mode: ThemeMode) => {
  const isLight = mode === "light";
  const primary = isLight ? BRAND_PRIMARY : BRAND_PRIMARY_DARK;
  const divider = isLight ? "#EAEAF4" : "rgba(255,255,255,0.08)";
  const shadows = buildShadows(isLight);

  return createTheme(
    {
      palette: {
        mode,
        primary: {
          main: primary.main,
          light: primary.light,
          dark: primary.dark,
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: isLight ? amber[700] : amber[300],
        },
        // Artifact'teki tonlar — chip/stripe/delta/canlı-pill hepsi bunları kullanır.
        success: { main: isLight ? "#0E9C86" : "#35D6BE" },
        warning: { main: isLight ? "#BE7A00" : "#F0B429" },
        error: { main: isLight ? "#DA3B44" : "#FF6B72" },
        info: { main: isLight ? "#4C77DC" : "#7FA2F0" },
        divider,
        ...(isLight
          ? {
              background: { default: "#F5F5FB", paper: "#FFFFFF" },
              text: { primary: "#17172A", secondary: "#6A6A82" },
            }
          : {
              background: { default: "#0C0C13", paper: "#15151F" },
              text: { primary: "#ECECF6", secondary: "#9C9CB4" },
            }),
      },
      shape: { borderRadius: 12 },
      shadows,
      typography: {
        // Artifact sistem fontuyla (SF Pro / -apple-system) render olmuştu; onunla birebir olması için
        // sistem fontunu öne al, Inter (rsms.me) Apple-dışı platformlarda yedek kalsın.
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', 'Inter', Roboto, system-ui, sans-serif",
        h1: { fontWeight: 800, letterSpacing: "-0.03em" },
        h2: { fontWeight: 800, letterSpacing: "-0.025em" },
        h3: { fontWeight: 800, letterSpacing: "-0.025em" },
        h4: { fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-0.025em" },
        h5: { fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.02em" },
        h6: { fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em" },
        subtitle1: { fontWeight: 600, letterSpacing: "-0.01em" },
        subtitle2: { fontWeight: 600 },
        body1: { letterSpacing: "-0.005em" },
        body2: { letterSpacing: "-0.005em" },
        button: { textTransform: "none", fontWeight: 650, letterSpacing: 0 },
        overline: { fontWeight: 700, letterSpacing: "0.09em", fontSize: "0.66rem" },
        caption: { letterSpacing: 0 },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            "*::-webkit-scrollbar": { width: 10, height: 10 },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: isLight ? "rgba(23,23,60,.18)" : "rgba(255,255,255,.16)",
              borderRadius: 8,
              border: `2px solid transparent`,
              backgroundClip: "content-box",
            },
            "*::-webkit-scrollbar-thumb:hover": {
              backgroundColor: isLight ? "rgba(23,23,60,.3)" : "rgba(255,255,255,.28)",
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            // Karanlık modda MUI'nin elevation overlay'ini kapat — yüzeyler token rengini korur,
            // özel gölge skalası temiz okunur.
            root: { backgroundImage: "none" },
            outlined: ({ theme }: { theme: Theme }) => ({
              borderColor: theme.palette.divider,
              boxShadow: theme.shadows[1],
            }),
          },
        },
        // ⭐ KART KENAR YARIÇAPI STANDARDI: kart yüzeyleri için DAİMA <Card> kullan (14px, outlined).
        // Paper'a `sx={{ borderRadius: 2/3 }}` VERME — shape.borderRadius=12 ile 24/36px olur (çok yuvarlak).
        // Diğer yarıçaplar: Dialog 18 · Menu/Paper 12 · Buton 10 · Input 10 · Chip/Tooltip 8.
        MuiCard: {
          defaultProps: { variant: "outlined" },
          styleOverrides: {
            root: ({ theme }: { theme: Theme }) => ({
              borderRadius: 14,
              borderColor: theme.palette.divider,
              boxShadow: theme.shadows[1],
              transition: theme.transitions.create(["box-shadow", "border-color"], { duration: 180 }),
            }),
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: { borderRadius: 10, paddingInline: 15 },
            sizeSmall: { borderRadius: 8 },
            containedPrimary: ({ theme }: { theme: Theme }) => ({
              boxShadow: theme.shadows[1],
              "&:hover": { boxShadow: theme.shadows[3] },
            }),
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { borderRadius: 8, fontWeight: 600 },
            sizeSmall: { fontSize: "0.72rem" },
            outlined: ({ theme }: { theme: Theme }) => ({ borderColor: theme.palette.divider }),
          },
        },
        MuiOutlinedInput: {
          styleOverrides: { root: { borderRadius: 10 } },
        },
        // Form kontrolleri: uygulamanın gövde metni body2 (0.875rem / 14px). MUI varsayılan olarak
        // girişlere body1 (1rem / 16px) uygular → textbox/etiket/checkbox/combobox metni geri
        // kalandan büyük görünüyordu (bu yüzden onlarca ekranda inputProps ile elle küçültülmüştü).
        // Hepsini tek yerden 0.875rem'e sabitle — buton ve body2 ile birebir.
        MuiInputBase: {
          styleOverrides: { root: { fontSize: "0.875rem" } },
        },
        MuiInputLabel: {
          styleOverrides: { root: { fontSize: "0.875rem" } },
        },
        MuiFormControlLabel: {
          styleOverrides: { label: { fontSize: "0.875rem" } },
        },
        MuiMenuItem: {
          styleOverrides: { root: { fontSize: "0.875rem" } },
        },
        MuiAutocomplete: {
          styleOverrides: { option: { fontSize: "0.875rem" } },
        },
        MuiDialog: {
          styleOverrides: {
            paper: ({ theme }: { theme: Theme }) => ({
              borderRadius: 18,
              boxShadow: theme.shadows[6],
              backgroundImage: "none",
            }),
          },
        },
        MuiDialogTitle: {
          styleOverrides: { root: { fontWeight: 700, fontSize: "1.1rem", letterSpacing: "-0.01em" } },
        },
        MuiDialogActions: {
          styleOverrides: { root: { padding: 16, gap: 8 } },
        },
        MuiMenu: {
          styleOverrides: {
            paper: ({ theme }: { theme: Theme }) => ({
              borderRadius: 12,
              boxShadow: theme.shadows[5],
              border: `1px solid ${theme.palette.divider}`,
              backgroundImage: "none",
            }),
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              borderRadius: 8,
              fontSize: "0.72rem",
              fontWeight: 500,
              padding: "6px 10px",
              backgroundColor: isLight ? "rgba(23,23,42,.92)" : "rgba(40,40,56,.98)",
            },
            arrow: { color: isLight ? "rgba(23,23,42,.92)" : "rgba(40,40,56,.98)" },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: ({ theme }: { theme: Theme }) => ({
              "&.Mui-selected": {
                backgroundColor: isLight ? "rgba(108,92,231,.10)" : "rgba(139,124,240,.15)",
                color: theme.palette.primary.main,
                "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
                "&:hover": {
                  backgroundColor: isLight ? "rgba(108,92,231,.16)" : "rgba(139,124,240,.22)",
                },
              },
            }),
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: ({ theme }: { theme: Theme }) => ({ borderColor: theme.palette.divider }),
            head: { fontWeight: 700 },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: ({ theme }: { theme: Theme }) => ({
              border: "none",
              "--DataGrid-rowBorderColor": theme.palette.divider,
              "& .MuiDataGrid-columnHeaders": { backgroundColor: isLight ? "#FAFAFE" : "#1B1B27" },
              "& .MuiDataGrid-columnHeader": { backgroundColor: isLight ? "#FAFAFE" : "#1B1B27" },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: "0.72rem",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: theme.palette.text.secondary,
              },
              "& .MuiDataGrid-cell": { fontSize: "0.82rem" },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: isLight ? "#F3F2FB" : "#21212F",
              },
              "& .MuiDataGrid-footerContainer": { borderTopColor: theme.palette.divider },
            }),
          },
        },
      },
    },
    coreTrTR,
    gridTrTR,
  );
};
